import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getAppTheme } from './theme';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeModeContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login/Login';
import Home from './components/Home/Home';
import CouponsList from './components/Coupons/CouponsList';
import NotificationsList from './components/Notifications/NotificationsList';
import EchoPaymentsList from './components/Payments/EchoPaymentsList';
import UserManagement from './components/Users/UserManagement';
import Reports from './components/Reports/Reports';
import DevicesList from './components/Devices/DevicesList';
import SiteVisit from './components/Visits/SiteVisit';
import ManualSiteVisit from './components/Visits/ManualSiteVisit';
import CustomPushPage from './components/CustomPush/CustomPushPage';
import PushDashboardPage from './components/PushDashboard/PushDashboardPage';
import StoreListPage from './components/Signage/StoreListPage';
import StorePlaylistPage from './components/Signage/StorePlaylistPage';

// Component inside Router to access useNavigate
const AuthProviderWithNavigate: React.FC<{ onSessionExpired: (message: string) => void }> = ({ onSessionExpired }) => {
  const navigate = React.useCallback((path: string) => {
    // Use window.location for navigation to ensure full page reload and clear state
    window.location.href = path;
  }, []);

  return (
    <AuthProvider onSessionExpired={onSessionExpired} navigate={navigate}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Home />} />
          <Route
            path="coupons"
            element={
              <ProtectedRoute roles={['coupon:view', 'coupon:tester', 'coupon:edit', 'coupon:admin']}>
                <CouponsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="notifications"
            element={
              <ProtectedRoute roles={['notification:view', 'notification:tester', 'notification:edit', 'notification:admin']}>
                <NotificationsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="payments"
            element={
              <ProtectedRoute roles={['admin']}>
                <EchoPaymentsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="users"
            element={
              <ProtectedRoute roles={['admin']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="redemptions"
            element={
              <ProtectedRoute roles={['reports', 'redeemer']}>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="devices"
            element={
              <ProtectedRoute roles={['token:view']}>
                <DevicesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="visits"
            element={
              <ProtectedRoute roles={['visit:view']}>
                <SiteVisit />
              </ProtectedRoute>
            }
          />
          <Route
            path="recordVisits"
            element={
              <ProtectedRoute roles={['visit:view']}>
                <ManualSiteVisit />
              </ProtectedRoute>
            }
          />
          <Route
            path="custom-push"
            element={
              <ProtectedRoute roles={['custom-push:view', 'custom-push:admin']}>
                <CustomPushPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="push-dashboard"
            element={
              <ProtectedRoute roles={['custom-push:view', 'custom-push:admin', 'notification:view']}>
                <PushDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="signage"
            element={
              <ProtectedRoute roles={['signage:view', 'signage:admin', 'admin']}>
                <StoreListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="signage/:storeId"
            element={
              <ProtectedRoute roles={['signage:view', 'signage:admin', 'admin']}>
                <StorePlaylistPage />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
};

// Inner component to access toast context - must be inside ToastProvider
const AppContent: React.FC = () => {
  const { showToast } = useToast();

  const handleSessionExpired = React.useCallback((message: string) => {
    showToast(message, 'warning');
  }, [showToast]);

  return (
    <Router>
      <AuthProviderWithNavigate onSessionExpired={handleSessionExpired} />
    </Router>
  );
};

// Applies the MUI theme once the resolved light/dark mode is known
const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { resolvedMode } = useThemeMode();
  const theme = React.useMemo(() => getAppTheme(resolvedMode), [resolvedMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </ThemedApp>
    </ThemeModeProvider>
  );
}

export default App;
